var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import bind from 'bind-decorator';
import { configure } from 'mobx';
import { system } from '@config/system';
import { setupQueryCache } from '@data/api/queryClient';
import { asyncSuspense } from '@data/other/asyncSuspense';
import { FeatureFlag, FeatureFlagManager } from '@business/feature-flags';
import OrganizationStore from './Organization';
import Components from './Components';
import Environment from './Environment';
import Property from './Property';
import Session from './Session';
import SmartPolicies from './SmartPolicies';
import { persistentPropertyStorage } from './PersistentPropertyStorage';
configure({ isolateGlobalState: true });
const normalizeStoredPromo = (saved) => saved['promo'] || saved['allotment_block_code'] || '';
export class Store {
    constructor() {
        var _a;
        Object.defineProperty(this, "components", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Components()
        });
        Object.defineProperty(this, "environment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Environment()
        });
        /** Last in-app route (MemoryRouter) for immersive persistence when cart saves without navigation */
        Object.defineProperty(this, "immersiveRouterSnapshot", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // Only used by the organization booking engine entrypoint; `organizationId` is
        // empty in the hosted app, where this store is never read.
        Object.defineProperty(this, "organization", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new OrganizationStore((_a = system.organizationId) !== null && _a !== void 0 ? _a : '')
        });
        Object.defineProperty(this, "property", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Property()
        });
        Object.defineProperty(this, "session", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Session()
        });
        Object.defineProperty(this, "smartPolicies", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new SmartPolicies()
        });
    }
    applyPersistedStoresFromSession() {
        this.property.load();
        this.session.load();
        this.components.load();
        this.smartPolicies.load();
    }
    clearPersistentData() {
        persistentPropertyStorage.clear();
    }
    // Per-instance stores (web components) subscribe to the global event
    // system; call on unmount or listeners outlive the component.
    dispose() {
        this.components.payment.dispose();
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield setupQueryCache();
            return this;
        });
    }
    isImmersivePersistenceEnabled() {
        return (system.isImmersiveExperience &&
            FeatureFlagManager.isEnabled(FeatureFlag.persistSearchOnImmersiveExperience));
    }
    /**
     * Restores sessionStorage-backed state for Immersive Experience when the flag is on.
     * If the URL already encodes dates, they must match storage (URL wins on conflict).
     */
    loadImmersivePersistedData({ checkin, checkout, promoCode, urlHasSearchDates, }) {
        if (!this.isImmersivePersistenceEnabled()) {
            return false;
        }
        const savedSearchParams = persistentPropertyStorage.getSearchParams();
        const savedCheckin = savedSearchParams['checkin'];
        const savedCheckout = savedSearchParams['checkout'];
        const savedPromo = normalizeStoredPromo(savedSearchParams);
        if (!savedCheckin || !savedCheckout) {
            return false;
        }
        if (urlHasSearchDates) {
            if (!checkin || !checkout) {
                return false;
            }
            const urlPromo = promoCode || '';
            const promosMatch = urlPromo === (savedPromo || '');
            if (checkin !== savedCheckin ||
                checkout !== savedCheckout ||
                !promosMatch) {
                persistentPropertyStorage.clear();
                return false;
            }
        }
        this.applyPersistedStoresFromSession();
        return true;
    }
    loadPersistentData({ checkin, checkout, promoCode, }) {
        // Verify the data from "SearchPanel" store without apply it
        // If the data is different, do not load the data
        const savedSearchParams = persistentPropertyStorage.getSearchParams();
        const savedCheckin = savedSearchParams['checkin'];
        const savedCheckout = savedSearchParams['checkout'];
        const savedCode = savedSearchParams['promo'];
        // If there no stored checkin and checkout, do not load the data
        if (!checkin || !checkout || !savedCheckin || !savedCheckout) {
            return false;
        }
        // Verify if the checkin, checkout and promocode are the same as the stored values
        if (checkin !== savedCheckin ||
            checkout !== savedCheckout ||
            promoCode !== savedCode) {
            return false;
        }
        this.applyPersistedStoresFromSession();
        return true;
    }
    savePersistentData() {
        if (this.isImmersivePersistenceEnabled()) {
            if (this.immersiveRouterSnapshot) {
                this.savePersistentDataFromRouter(this.immersiveRouterSnapshot.pathname, this.immersiveRouterSnapshot.search);
            }
            else {
                // No router snapshot yet – persist stores without overwriting
                // search params / pathname that may already exist in storage.
                persistentPropertyStorage.touch();
                this.property.save();
                this.session.save();
                this.components.save();
                this.smartPolicies.save();
            }
            return;
        }
        persistentPropertyStorage.setSearchParams(window.location.search);
        this.property.save();
        this.session.save();
        this.components.save();
        this.smartPolicies.save();
    }
    /**
     * Persists MobX stores + router search/path for Immersive (MemoryRouter).
     */
    savePersistentDataFromRouter(pathname, search) {
        if (!this.isImmersivePersistenceEnabled()) {
            return;
        }
        this.immersiveRouterSnapshot = { pathname, search };
        const normalizedSearch = !search || search.startsWith('?') ? search || '' : `?${search}`;
        persistentPropertyStorage.setSearchParams(normalizedSearch);
        persistentPropertyStorage.setRouterPathname(pathname);
        this.property.save();
        this.session.save();
        this.components.save();
        this.smartPolicies.save();
    }
}
__decorate([
    bind,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Store.prototype, "init", null);
const store = new Store();
export const initializeStore = asyncSuspense(store.init, 'store');
// Used by the integration tests to reset the store before mounting the App
export const resetStore = () => {
    var _a;
    store.dispose();
    store.organization = new OrganizationStore((_a = system.organizationId) !== null && _a !== void 0 ? _a : '');
    store.components = new Components();
    store.environment = new Environment();
    store.property = new Property();
    store.session = new Session();
    store.smartPolicies = new SmartPolicies();
    store.immersiveRouterSnapshot = null;
    store.clearPersistentData();
};
export default store;
